<script>
export default {
  props: {
    hasLegalRepresentative: {
      type: Boolean,
      default: false
    },
    hasFinancialRepresentative: {
      type: Boolean,
      default: false
    },
    hasFirstForumRepresentative: {
      type: Boolean,
      default: false
    },
    hasSecondForumRepresentative: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      representative: {
        name: '',
        cpf: '',
        cellphone: '',
        birthdate: '',
        email: '',
        position: ''
      },
      isLegalRepresentative: false,
      isFinancialRepresentative: false,
      isForumRepresentative: false,
      isAttorney: false,
      editingRoleKeys: [],
      attorneyFile: null
    }
  },

  computed: {
    forumRepresentativeLimitReached() {
      return this.hasFirstForumRepresentative && this.hasSecondForumRepresentative
    },

    financialRepresentativeDisabled() {
      return this.hasFinancialRepresentative && !this.editingRoleKeys.includes('financial')
    },

    attorneyDisabled() {
      return this.hasLegalRepresentative && !this.editingRoleKeys.includes('attorney')
    },

    forumRepresentativeDisabled() {
      return this.forumRepresentativeLimitReached && !this.editingRoleKeys.some((roleKey) => roleKey.startsWith('forum_'))
    },

    nextForumRoleKey() {
      if (!this.hasFirstForumRepresentative) return 'forum_1'
      if (!this.hasSecondForumRepresentative) return 'forum_2'

      return null
    },

    activeForumRoleKey() {
      return this.editingRoleKeys.find((roleKey) => roleKey.startsWith('forum_')) || this.nextForumRoleKey
    },

    forumResponsibleFieldPrefix() {
      if (this.activeForumRoleKey === 'forum_1') return 'responsible_1'
      if (this.activeForumRoleKey === 'forum_2') return 'responsible_2'

      return null
    },

    selectedRoleKeys() {
      const roleKeys = []

      if (this.isLegalRepresentative) roleKeys.push('legal')
      if (this.isFinancialRepresentative) roleKeys.push('financial')
      if (this.isAttorney) roleKeys.push('attorney')
      if (this.isForumRepresentative && this.activeForumRoleKey) roleKeys.push(this.activeForumRoleKey)

      return roleKeys
    },

    saveButtonText() {
      return this.editingRoleKeys.length > 0 ? 'Salvar' : 'Adicionar'
    }
  },

  watch: {
    isAttorney(value) {
      if (value) this.isLegalRepresentative = false
    },

    isLegalRepresentative(value) {
      if (value) this.isAttorney = false
    }
  },

  mounted() {
    document.addEventListener('click', this.handleRepresentativeClick)
    this.initTooltips()
  },

  beforeDestroy() {
    document.removeEventListener('click', this.handleRepresentativeClick)
    this.destroyTooltips()
  },

  updated() {
    this.initTooltips()
  },

  methods: {
    initTooltips() {
      this.destroyTooltips()
      const tooltipTriggerList = this.$el.querySelectorAll('[data-bs-toggle="tooltip"]')
      this.tooltipInstances = [...tooltipTriggerList].map(tooltipTriggerEl => {
        if (typeof bootstrap !== 'undefined') {
          return new bootstrap.Tooltip(tooltipTriggerEl)
        }
        return null
      }).filter(Boolean)
    },

    destroyTooltips() {
      if (this.tooltipInstances && this.tooltipInstances.length) {
        this.tooltipInstances.forEach(tooltip => tooltip.dispose())
        this.tooltipInstances = []
      }
    },
    addRepresentative() {
      const list = document.getElementById('representativesList')

      if (!list || !this.representative.name) return
      if (this.financialRepresentativeDisabled) this.isFinancialRepresentative = false
      if (this.attorneyDisabled) this.isAttorney = false
      if (this.forumRepresentativeDisabled) this.isForumRepresentative = false

      const previousDraft = list.querySelector('[data-modal-representative="true"]')
      if (previousDraft) previousDraft.remove()

      const badges = this.representativeBadges()
      const details = [
        this.representative.position,
        this.representative.email,
        this.representative.cellphone
      ].filter(Boolean).join(' / ')

      const card = document.createElement('div')
      card.className = 'representative-card p-3 mb-4 position-relative border rounded'
      card.dataset.modalRepresentative = 'true'
      card.innerHTML = `
        <div class="d-flex justify-content-between align-items-start">
          <div>
            <h5 class="fw-bold mb-1 text-dark text-capitalize">${this.escapeHtml(this.representative.name)}</h5>
            <p class="text-muted small mb-3">${this.escapeHtml(details)}</p>
          </div>

          <div class="card-actions">
            <button type="button" class="btn btn-link text-secondary p-1 me-2" data-bs-toggle="modal" data-bs-target="#representativeModal">
              <i class="bi bi-pencil-square fs-5"></i>
            </button>

            <button type="button" class="btn btn-link text-danger p-1" data-remove-modal-representative>
              <i class="bi bi-trash fs-5"></i>
            </button>
          </div>
        </div>

        <div class="d-flex flex-wrap gap-2">
          ${badges.map((badge) => `<span class="badge badge-purple">${this.escapeHtml(badge)}</span>`).join('')}
        </div>
      `

      card
        .querySelector('[data-remove-modal-representative]')
        .addEventListener('click', () => {
          card.remove()
          this.clearRepresentative()
        })

      list.appendChild(card)
    },

    handleRepresentativeClick(event) {
      const editButton = event.target.closest('[data-edit-representative]')
      const removeButton = event.target.closest('[data-remove-persisted-representative]')
      const newButton = event.target.closest('[data-new-representative]')

      if (editButton) {
        this.fillRepresentativeFromCard(editButton.closest('[data-representative-card]'))
        return
      }

      if (removeButton) {
        this.removePersistedRepresentative(removeButton.closest('[data-representative-card]'))
        return
      }

      if (newButton) this.clearRepresentative()
    },

    fillRepresentativeFromCard(card) {
      if (!card || !card.dataset.representative) return

      const data = JSON.parse(card.dataset.representative)
      const roleKeys = data.role_keys || []

      this.representative = {
        name: data.name || '',
        cpf: data.cpf || '',
        cellphone: data.cellphone || '',
        birthdate: data.birthdate || '',
        email: data.email || '',
        position: data.position || ''
      }
      this.editingRoleKeys = roleKeys
      this.isLegalRepresentative = roleKeys.includes('legal') || roleKeys.includes('legal_not_user')
      this.isFinancialRepresentative = roleKeys.includes('financial')
      this.isForumRepresentative = roleKeys.some((roleKey) => roleKey.startsWith('forum_'))
      this.isAttorney = roleKeys.includes('attorney')
    },

    removePersistedRepresentative(card) {
      if (!card || !card.dataset.representative) return
      if (!window.confirm('Remover este representante?')) return

      const form = card.closest('form')
      const data = JSON.parse(card.dataset.representative)
      const roleKeys = data.role_keys || []

      if (!form || roleKeys.length === 0) return

      form.querySelectorAll('[data-dynamic-representative-input]').forEach((input) => input.remove())
      roleKeys.forEach((roleKey) => {
        const input = document.createElement('input')
        input.type = 'hidden'
        input.name = 'representative[role_keys][]'
        input.value = roleKey
        input.dataset.dynamicRepresentativeInput = 'true'
        form.appendChild(input)
      })

      form.action = '/search_profile/representative/remove'
      form.method = 'post'
      form.submit()
    },

    clearRepresentative() {
      this.representative = {
        name: '',
        cpf: '',
        cellphone: '',
        birthdate: '',
        email: '',
        position: ''
      }
      this.isLegalRepresentative = false
      this.isFinancialRepresentative = false
      this.isForumRepresentative = false
      this.isAttorney = false
      this.editingRoleKeys = []
      this.attorneyFile = null
    },

    representativeBadges() {
      const badges = []

      if (this.isLegalRepresentative) badges.push('Responsável Legal')
      if (this.isFinancialRepresentative) badges.push('Rep. Financeiro')
      if (this.isAttorney) badges.push('Procurador')
      if (this.isForumRepresentative) badges.push('Rep. do Fórum')

      return badges
    },

    escapeHtml(value) {
      return String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;')
    },

    handleFile(event) {
      this.attorneyFile = event.target.files[0]
    }
  }
}
</script>
